import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faArrowsRotate, faLayerGroup, faBoxArchive, faRotateLeft } from '@fortawesome/free-solid-svg-icons';
import Table from 'react-bootstrap/Table';
import { getFirestore } from "@firebase/firestore";
import { app } from '../../firebase.js';
import { collection, doc, setDoc, query, getDocs, updateDoc, Timestamp } from "@firebase/firestore";
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import swal from 'sweetalert';
import Spinner from 'react-bootstrap/Spinner';
import { useNavigate } from 'react-router-dom';
import { logAdminActivity } from '../../utils/activityLogger';

function CoursesComp() {
  
    const db = getFirestore(app);
    let navigate = useNavigate();
  
    const [studentsList, setStudentList] = useState([]);
    const [courseStudentCounts, setCourseStudentCounts] = useState({});
    const [searchTerm, setSearchTerm] = useState('');
    const [showArchivedCourses, setShowArchivedCourses] = useState(false);
    const [modalShowAdd, setModalShowAdd] = useState(false);
    const [modalShowEdit, setModalShowEdit] = useState(false);
    const [selectedCourse, setSelectedCourse] = useState(null);
    const [courseToArchive, setCourseToArchive] = useState(null);
    const [loading, setLoading] = useState(false);
    const [updatingCourseId, setUpdatingCourseId] = useState('');

    const normalizeRank = (value) => {
      const numeric = Number(value);
      return Number.isFinite(numeric) ? numeric : Number.POSITIVE_INFINITY;
    };

    const normalizeCourseName = (value) => String(value || '').trim().toLowerCase();

    const getStudentCourseName = (student = {}) => (
      student.course
      || student.program
      || student.programme
      || student.CourseName
      || student.courseName
      || ''
    );

    const [newCourse, setNewCourse] = useState({
      mcourseName: '',
      mcourseDescription: '',
      mcourseDuration: '',
      courseDuration: '',
      mcoursePrice: '',
      feeDeposit: '',
      feeInstallments: '',
      groupLink: '',
      rank: ''
    });

    const [editCourse, setEditCourse] = useState({
      mcourseName: '',
      mcourseDescription: '',
      mcourseDuration: '',
      courseDuration: '',
      mcoursePrice: '',
      feeDeposit: '',
      feeInstallments: '',
      groupLink: '',
      rank: ''
    });

    const fetchData = useCallback(async () => {
      const [coursesSnapshot, studentsSnapshot] = await Promise.all([
        getDocs(query(collection(db, 'courses'))),
        getDocs(query(collection(db, 'users')))
      ]);

      const studentsItem = [];
      coursesSnapshot.forEach((doc) => {
        studentsItem.push({ id: doc.id, ...doc.data() });
      });
      studentsItem.sort((first, second) => {
        const rankA = normalizeRank(first.rank);
        const rankB = normalizeRank(second.rank);
        if (rankA !== rankB) {
          return rankA - rankB;
        }

        return String(first.mcourseName || '').localeCompare(String(second.mcourseName || ''));
      });

      const courseDefinitions = studentsItem.map((course) => ({
        id: course.id,
        name: course.mcourseName || '',
        normalizedName: normalizeCourseName(course.mcourseName)
      }));

      const nextCounts = {};
      courseDefinitions.forEach((course) => {
        nextCounts[course.id] = 0;
      });

      studentsSnapshot.forEach((docItem) => {
        const data = docItem.data();
        const studentCourse = normalizeCourseName(getStudentCourseName(data));
        if (!studentCourse) {
          return;
        }

        const candidateMatches = courseDefinitions
          .map((course) => {
            const exact = studentCourse === course.normalizedName;
            const includes = studentCourse.includes(course.normalizedName) || course.normalizedName.includes(studentCourse);
            const score = exact ? 0 : includes ? 1 : 2;
            return { course, score };
          })
          .filter((item) => item.score < 2)
          .sort((left, right) => left.score - right.score || left.course.name.length - right.course.name.length);

        const bestMatch = candidateMatches[0]?.course;
        if (bestMatch) {
          nextCounts[bestMatch.id] = (nextCounts[bestMatch.id] || 0) + 1;
        }
      });

      setStudentList(studentsItem);
      setCourseStudentCounts(nextCounts);
    }, [db]);
  
    useEffect(() => {
      fetchData();
    }, [fetchData]);

    const activeCourses = useMemo(
      () => studentsList.filter((course) => !course.isArchived),
      [studentsList]
    );
    const archivedCourses = useMemo(
      () => studentsList.filter((course) => course.isArchived),
      [studentsList]
    );

    const filteredCourses = useMemo(() => {
      const queryText = searchTerm.trim().toLowerCase();
      const visibleCourses = showArchivedCourses ? archivedCourses : activeCourses;

      return visibleCourses.filter((course) => {
        if (!queryText) {
          return true;
        }

        const haystack = [
          course.mcourseName,
          course.mcourseDuration,
          course.courseDuration,
          course.mcoursePrice,
          course.feeDeposit,
          course.feeInstallments,
          course.rank
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();

        return haystack.includes(queryText);
      });
    }, [activeCourses, archivedCourses, searchTerm, showArchivedCourses]);

    const courseStats = useMemo(() => {
      const ranked = activeCourses.filter((course) => Number.isFinite(Number(course.rank))).length;
      const unrated = activeCourses.length - ranked;
      const topRank = activeCourses.find((course) => Number.isFinite(Number(course.rank)))?.rank ?? '—';
      const totalLinkedStudents = Object.values(courseStudentCounts).reduce((sum, value) => sum + Number(value || 0), 0);

      return {
        ranked,
        unrated,
        topRank,
        totalLinkedStudents
      };
    }, [activeCourses, courseStudentCounts]);

    // Helper function to format amounts
    function formatAmount(amount) {
        if (amount === null || amount === undefined || amount === '') return "";
        const normalized = typeof amount === 'number' ? amount : parseFloat(String(amount).replace(/,/g, ""));
        if (Number.isNaN(normalized)) return amount;
        return new Intl.NumberFormat().format(normalized);
    }

    const openViewModal = (course) => {
      navigate(`/course-details/${course.id}`);
    };

    const openEditModal = (course) => {
      setSelectedCourse(course);
      setEditCourse({
        mcourseName: course.mcourseName || '',
        mcourseDescription: course.mcourseDescription || '',
        mcourseDuration: course.mcourseDuration || '',
        courseDuration: course.courseDuration || '',
        mcoursePrice: course.mcoursePrice || '',
        feeDeposit: course.feeDeposit || '',
        feeInstallments: course.feeInstallments || '',
        groupLink: course.groupLink || '',
        rank: course.rank ?? ''
      });
      setModalShowEdit(true);
    };

    const handleNewCourseChange = (field, value) => {
      setNewCourse((prev) => ({ ...prev, [field]: value }));
    };

    const handleEditCourseChange = (field, value) => {
      setEditCourse((prev) => ({ ...prev, [field]: value }));
    };

    const submitNewCourse = async () => {
      if (!newCourse.mcourseName.trim()) {
        swal("Course Name Required", "Please enter the course name.", "error");
        return;
      }
      setLoading(true);
      try {
        const newDataRef = doc(collection(db, "courses"));
        await setDoc(newDataRef, {
          ...newCourse,
          courseId: newDataRef.id,
          rank: newCourse.rank === '' ? null : Number(newCourse.rank),
          isArchived: false,
          archivedAt: null
        });
        logAdminActivity({
          action: 'Add Course',
          details: `Added course ${newCourse.mcourseName}`,
          meta: {
            page: 'Courses',
            courseId: newDataRef.id,
            courseName: newCourse.mcourseName
          }
        });
        setNewCourse({
          mcourseName: '',
          mcourseDescription: '',
          mcourseDuration: '',
          courseDuration: '',
          mcoursePrice: '',
          feeDeposit: '',
          feeInstallments: '',
          groupLink: '',
          rank: ''
        });
        setModalShowAdd(false);
        fetchData();
      } catch (error) {
        swal("Error!", "Failed to add course: " + error.message, "error");
      } finally {
        setLoading(false);
      }
    };

    const submitEditCourse = async () => {
      if (!selectedCourse?.id) return;
      if (!editCourse.mcourseName.trim()) {
        swal("Course Name Required", "Please enter the course name.", "error");
        return;
      }
      setLoading(true);
      try {
        const courseRef = doc(db, "courses", selectedCourse.id);
        await updateDoc(courseRef, {
          ...editCourse,
          courseId: selectedCourse.courseId || selectedCourse.id,
          rank: editCourse.rank === '' ? null : Number(editCourse.rank)
        });
        logAdminActivity({
          action: 'Update Course',
          details: `Updated course ${editCourse.mcourseName}`,
          meta: {
            page: 'Courses',
            courseId: selectedCourse.courseId || selectedCourse.id,
            courseName: editCourse.mcourseName
          }
        });
        setModalShowEdit(false);
        fetchData();
      } catch (error) {
        swal("Error!", "Failed to update course: " + error.message, "error");
      } finally {
        setLoading(false);
      }
    };

    const updateCourseArchiveStatus = async (course, isArchived) => {
      setUpdatingCourseId(course.id);
      try {
        const archivedAt = isArchived ? Timestamp.now() : null;
        await updateDoc(doc(db, 'courses', course.id), {
          isArchived,
          archivedAt,
          updatedAt: Timestamp.now()
        });

        setStudentList((currentCourses) => currentCourses.map((currentCourse) => (
          currentCourse.id === course.id
            ? { ...currentCourse, isArchived, archivedAt }
            : currentCourse
        )));

        await logAdminActivity({
          action: isArchived ? 'Archived Course' : 'Restored Course',
          details: `${isArchived ? 'Archived' : 'Restored'} course ${course.mcourseName || ''}`.trim(),
          meta: {
            page: 'Courses',
            courseId: course.courseId || course.id,
            courseName: course.mcourseName || ''
          }
        });
        if (isArchived) {
          setCourseToArchive(null);
        }
      } catch (error) {
        console.error('Failed to update course archive status:', error);
        swal('Error', `Failed to ${isArchived ? 'archive' : 'restore'} course.`, 'error');
      } finally {
        setUpdatingCourseId('');
      }
    };

    const toggleCourseArchive = (course) => {
      if (course.isArchived) {
        updateCourseArchiveStatus(course, false);
        return;
      }

      setCourseToArchive(course);
    };

    const CourseFormFields = ({ values, onChange }) => (
      <>
        <Form.Group className="mb-3">
          <Form.Label>Course Name</Form.Label>
          <Form.Control type="text" value={values.mcourseName} onChange={(e) => onChange('mcourseName', e.target.value)} />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Course Description</Form.Label>
          <Form.Control as="textarea" rows={4} value={values.mcourseDescription} onChange={(e) => onChange('mcourseDescription', e.target.value)} />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Course Duration (Label)</Form.Label>
          <Form.Control type="text" placeholder="e.g. 10 Months" value={values.mcourseDuration} onChange={(e) => onChange('mcourseDuration', e.target.value)} />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Course Duration (Months)</Form.Label>
          <Form.Control type="number" placeholder="e.g. 10" value={values.courseDuration} onChange={(e) => onChange('courseDuration', e.target.value)} />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Total Fee</Form.Label>
          <Form.Control type="text" value={values.mcoursePrice} onChange={(e) => onChange('mcoursePrice', e.target.value)} />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Fee Deposit</Form.Label>
          <Form.Control type="text" value={values.feeDeposit} onChange={(e) => onChange('feeDeposit', e.target.value)} />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Fee Installments</Form.Label>
          <Form.Control type="text" value={values.feeInstallments} onChange={(e) => onChange('feeInstallments', e.target.value)} />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Group Link</Form.Label>
          <Form.Control type="text" value={values.groupLink} onChange={(e) => onChange('groupLink', e.target.value)} />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Rank</Form.Label>
          <Form.Control type="number" value={values.rank} onChange={(e) => onChange('rank', e.target.value)} />
        </Form.Group>
      </>
    );
  
    return (
      <div className="clientsComp coursesPage">
          <section className="appsHeader coursesHeader">
            <div className="attendanceHeaderCopy">
              <p className="appsKicker">Academic Setup</p>
              <h4>Courses</h4>
              <p className="appsSubtext">
                Courses are ordered by rank so the highest priority option stays at the top. Search by name, duration,
                fee, or rank to move quickly through the list.
              </p>
              <div className="attendanceHeaderTags">
                <span className="attendanceHeaderTag">Ranked list</span>
                <span className="attendanceHeaderTag">Minimal view</span>
                <span className="attendanceHeaderTag">Fast search</span>
              </div>
            </div>
            <div className="appsHeaderStats">
              <div className="appsStatChip">
                <span>Active courses</span>
                <strong>{activeCourses.length}</strong>
              </div>
              <div className="appsStatChip">
                <span>Ranked</span>
                <strong>{courseStats.ranked}</strong>
              </div>
              <div className="appsStatChip">
                <span>Unranked</span>
                <strong>{courseStats.unrated}</strong>
              </div>
              <div className="appsStatChip">
                <span>Linked records</span>
                <strong>{courseStats.totalLinkedStudents}</strong>
              </div>
            </div>
          </section>

          <section className="appsFiltersCard coursesFiltersCard">
            <div className="appsSearch input-group">
              <span className="input-group-text">
                <FontAwesomeIcon icon={faSearch} />
              </span>
              <Form.Control
                type="search"
                placeholder="Search course, duration, fee, or rank..."
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
              />
            </div>

            <div className="incomeToolbarMeta">
              <span className="incomeToolbarText">
                <FontAwesomeIcon icon={faLayerGroup} className="me-2" />
                Ordered by rank
              </span>
              <span className="incomeInlineDot" />
              <span className="incomeToolbarText">Top rank: {courseStats.topRank}</span>
            </div>

            <div className="coursesActionRow">
              <Button variant="outline-dark" onClick={fetchData}>
                <FontAwesomeIcon icon={faArrowsRotate} className="me-2" />
                Refresh
              </Button>
              <Button variant="primary" onClick={() => setModalShowAdd(true)}>Add New Course</Button>
            </div>

            <div className="coursesArchiveTabs" role="group" aria-label="Course list view">
              <Button
                variant={showArchivedCourses ? 'outline-secondary' : 'dark'}
                onClick={() => setShowArchivedCourses(false)}
                aria-pressed={!showArchivedCourses}
              >
                Active <span>{activeCourses.length}</span>
              </Button>
              <Button
                variant={showArchivedCourses ? 'dark' : 'outline-secondary'}
                onClick={() => setShowArchivedCourses(true)}
                aria-pressed={showArchivedCourses}
              >
                Archived <span>{archivedCourses.length}</span>
              </Button>
            </div>
          </section>

          <section className="appsTableCard coursesTableCard">
            <div className="appsTableHeader">
              <h6>{showArchivedCourses ? 'Archived Courses' : 'Active Courses'}</h6>
              <span>{filteredCourses.length} shown</span>
            </div>
            <div className="appsTableWrap">
              <Table hover responsive className="appsTable coursesTable">
                <thead>
                  <tr>
                    <th>Rank</th>
                    <th>Course Name</th>
                    <th>Duration</th>
                    <th>First Installment</th>
                    <th>Installments</th>
                    <th>Total Fee</th>
                    <th>Students</th>
                    <th>Edit</th>
                    <th>View</th>
                    <th>{showArchivedCourses ? 'Restore' : 'Archive'}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCourses.map((mystudent) => (
                    <tr key={mystudent.id}>
                      <td>
                        <span className="courseRankBadge">
                          {Number.isFinite(Number(mystudent.rank)) ? mystudent.rank : '—'}
                        </span>
                      </td>
                      <td>
                        <div className="attendanceRecordCell">
                          <strong>{mystudent.mcourseName}</strong>
                        </div>
                      </td>
                      <td>{mystudent.mcourseDuration}</td>
                      <td>KES {formatAmount(mystudent.feeDeposit)}</td>
                      <td>KES {formatAmount(mystudent.feeInstallments)}</td>
                      <td>KES {formatAmount(mystudent.mcoursePrice)}</td>
                      <td>
                        <span className="courseCountBadge">
                          {courseStudentCounts[mystudent.id] || 0}
                        </span>
                      </td>
                      <td>
                        <button className='btn btn-sm btn-warning' onClick={() => openEditModal(mystudent)}>
                          edit
                        </button>
                      </td>
                      <td>
                        <button className='btn btn-sm btn-primary' onClick={() => openViewModal(mystudent)}>
                          view
                        </button>
                      </td>
                      <td>
                        <Button
                          variant={showArchivedCourses ? 'outline-success' : 'outline-warning'}
                          size="sm"
                          disabled={updatingCourseId === mystudent.id}
                          onClick={() => toggleCourseArchive(mystudent)}
                        >
                          <FontAwesomeIcon icon={showArchivedCourses ? faRotateLeft : faBoxArchive} className="me-1" />
                          {updatingCourseId === mystudent.id
                            ? 'Saving...'
                            : showArchivedCourses ? 'Restore' : 'Archive'}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              {filteredCourses.length === 0 && (
                <div className="appsEmptyState">
                  {showArchivedCourses
                    ? 'No archived courses match the current search.'
                    : 'No active courses match the current search.'}
                </div>
              )}
            </div>
          </section>

          <Modal show={modalShowAdd} onHide={() => setModalShowAdd(false)} centered>
            <Modal.Header closeButton>
              <Modal.Title>Add New Course</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <CourseFormFields values={newCourse} onChange={handleNewCourseChange} />
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setModalShowAdd(false)}>Close</Button>
              <Button variant="primary" onClick={submitNewCourse} disabled={loading}>
                {loading ? (
                  <>
                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                    <span> Saving...</span>
                  </>
                ) : (
                  'Save Course'
                )}
              </Button>
            </Modal.Footer>
          </Modal>

          <Modal show={modalShowEdit} onHide={() => setModalShowEdit(false)} centered>
            <Modal.Header closeButton>
              <Modal.Title>Edit Course</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <CourseFormFields values={editCourse} onChange={handleEditCourseChange} />
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setModalShowEdit(false)}>Close</Button>
              <Button variant="primary" onClick={submitEditCourse} disabled={loading}>
                {loading ? (
                  <>
                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                    <span> Saving...</span>
                  </>
                ) : (
                  'Update Course'
                )}
              </Button>
            </Modal.Footer>
          </Modal>

          <Modal
            show={Boolean(courseToArchive)}
            onHide={() => {
              if (!updatingCourseId) setCourseToArchive(null);
            }}
            centered
          >
            <Modal.Header closeButton={!updatingCourseId}>
              <Modal.Title>Archive course?</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <p className="mb-2">
                Archive <strong>{courseToArchive?.mcourseName || 'this course'}</strong>?
              </p>
              <p className="text-muted mb-0">
                It will be removed from the active courses list and won’t be available when creating intakes. You can restore it later.
              </p>
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="outline-secondary"
                onClick={() => setCourseToArchive(null)}
                disabled={Boolean(updatingCourseId)}
              >
                Cancel
              </Button>
              <Button
                variant="warning"
                onClick={() => courseToArchive && updateCourseArchiveStatus(courseToArchive, true)}
                disabled={!courseToArchive || Boolean(updatingCourseId)}
              >
                {updatingCourseId ? 'Archiving...' : 'Archive Course'}
              </Button>
            </Modal.Footer>
          </Modal>

        </div>
  
    )
  }
   
export default CoursesComp
